/**
 * Color Selector Component
 *
 * Allows users to select product color variant
 */

import { motion } from 'framer-motion';
import type { ProductVariant } from './types';
import { StockIndicator } from './StockIndicator';

interface ColorSelectorProps {
  variants: ProductVariant[];
  selectedVariant: ProductVariant | null;
  onSelect: (variant: ProductVariant) => void;
}

export const ColorSelector = ({
  variants,
  selectedVariant,
  onSelect,
}: ColorSelectorProps) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">
        Choose Color <span className="text-red-500">*</span>
      </h3>

      <div className="grid grid-cols-3 gap-4">
        {variants.map((variant) => {
          const isSelected = selectedVariant?.id === variant.id;
          const isOutOfStock = variant.stock === 0;

          return (
            <motion.button
              key={variant.id}
              onClick={() => !isOutOfStock && onSelect(variant)}
              disabled={isOutOfStock}
              whileHover={!isOutOfStock ? { scale: 1.02 } : {}}
              whileTap={!isOutOfStock ? { scale: 0.98 } : {}}
              className={`
                relative p-4 border-2 rounded-lg transition-all
                ${isSelected
                  ? 'border-brand-primary bg-brand-primary/5 shadow-lg'
                  : 'border-gray-200 hover:border-brand-primary/50'
                }
                ${isOutOfStock ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {/* Color Preview */}
              <div
                className={`
                  w-full h-20 rounded-md mb-3
                  ${variant.color === 'Black' ? 'bg-gray-900' : ''}
                  ${variant.color === 'Purple' ? 'bg-purple-600' : ''}
                  ${variant.color === 'Orange' ? 'bg-orange-500' : ''}
                `}
              />

              {/* Color Name */}
              <div className="font-medium text-gray-900 mb-2">{variant.color}</div>

              {/* Stock Indicator */}
              <StockIndicator stock={variant.stock} size="sm" />

              {/* Selected Indicator */}
              {isSelected && (
                <motion.div
                  layoutId="selected-color"
                  className="absolute top-2 right-2 w-6 h-6 bg-brand-primary rounded-full flex items-center justify-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                >
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </motion.div>
              )}

              {/* Out of Stock Overlay */}
              {isOutOfStock && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
                  <span className="font-semibold text-gray-500">Out of Stock</span>
                </div>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};
